import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { request } from '../utils/request';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    emp_id: '',
    mobile_number: '',
    address: '',
    department: '',
    location: '',
    roles: [],
  });

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const timeA = a.createdAt
        ? new Date(a.createdAt).getTime()
        : (a._id && String(a._id).length >= 8 ? parseInt(String(a._id).substring(0, 8), 16) * 1000 : 0);
      const timeB = b.createdAt
        ? new Date(b.createdAt).getTime()
        : (b._id && String(b._id).length >= 8 ? parseInt(String(b._id).substring(0, 8), 16) * 1000 : 0);
      return timeB - timeA;
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return sorted;

    return sorted.filter((u) => {
      const username = (u.username || '').toLowerCase();
      const firstName = (u.first_name || '').toLowerCase();
      const lastName = (u.last_name || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const reverseFullName = `${lastName} ${firstName}`.trim();
      const email = (u.email || '').toLowerCase();
      const empId = (u.emp_id || u.employee_id || '').toLowerCase();
      const mobile = (u.mobile_number || u.mobile || u.phone || '').toLowerCase();
      const department = (u.department || '').toLowerCase();
      const roleNames = (u.roles || []).map(r => (r.name || r.role || '').toLowerCase()).join(' ');

      return (
        username.includes(q) ||
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q) ||
        reverseFullName.includes(q) ||
        email.includes(q) ||
        empId.includes(q) ||
        mobile.includes(q) ||
        department.includes(q) ||
        roleNames.includes(q)
      );
    });
  }, [sorted, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, users.length]);

  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleChangePage = (event, nextPage) => setPage(nextPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    const data = await request('users', { method: 'get' });
    setUsers(data);
  };

  const fetchRoles = async () => {
    const data = await request('roles', { method: 'get' });
    setRoles(data);
  };

  const handleOpen = (user = null) => {
    if (user) {
      setFormData({
        id: user._id,
        username: user.username,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        emp_id: user.emp_id || '',
        mobile_number: user.mobile_number || user.mobile || '',
        address: user.address || '',
        department: user.department || '',
        location: user.location || '',
        roles: (user.roles || []).map(r => (typeof r === 'object' ? r._id : r)),
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        email: '',
        emp_id: '',
        mobile_number: '',
        address: '',
        department: '',
        location: '',
        roles: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'mobile_number' || name === 'mobile') {
      const cleaned = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    if (name === 'emp_id') {
      const cleaned = value.slice(0, 8);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const payload = { ...formData, roles: formData.roles };
    const opts = isEditing
      ? { method: 'put', params: formData.id, body: payload }
      : { method: 'post', body: payload };
    await request('users', opts);
    fetchUsers();
    handleClose();
  };

  return (
    <Box p={2}>
      {/* Header, Search, and Add Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.9rem' }}>
          <Link href="/home" underline="hover" color="inherit" sx={{ fontSize: '0.9rem' }}>
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontSize: '0.9rem' }}>
            Users
          </Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name, email, emp ID, mobile..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#64748B' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    sx={{ color: '#64748B' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: { xs: 220, sm: 300, md: 340 },
              '& .MuiInputBase-root': {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                height: '36px',
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E2E8F0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#CBD5E1',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00bf63',
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, height: '36px', px: 2, whiteSpace: 'nowrap' }}
            onClick={() => handleOpen()}
          >
            Add User
          </Button>
        </Stack>
      </Stack>

      {/* Users Table */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <TableContainer>
          <Table className="global-table" size="small">
            <TableHead sx={{ backgroundColor: '#f0f7f5' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748B' }}>
                    {searchQuery ? (
                      <Stack spacing={1} alignItems="center" justifyContent="center">
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          No users found matching &ldquo;<strong>{searchQuery}</strong>&rdquo;
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setSearchQuery('')}
                          sx={{ textTransform: 'none', color: '#00bf63', fontSize: '0.85rem' }}
                        >
                          Clear Search
                        </Button>
                      </Stack>
                    ) : (
                      'No users found.'
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((u, idx) => (
                  <TableRow key={u._id} hover>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.department}</TableCell>
                    <TableCell>
                      {u.roles?.map(r => r.name).join(', ') || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleOpen(u)}
                        sx={{ color: '#00bf63', p: 0.5 }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* User Form Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
     <DialogTitle
  sx={{
    backgroundColor: '#00bf63',
    color: '#fff',
    fontSize: '1rem',
    py: 1,
    textAlign: 'center' // This centers the text
  }}
>
  {isEditing ? 'Edit User' : 'Add User'}
</DialogTitle>

        <DialogContent sx={{ p: 2 , mt: '10px'  }}>
          <Stack spacing={1}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Employee ID"
                name="emp_id"
                value={formData.emp_id}
                onChange={handleChange}
                inputProps={{ maxLength: 8 }}
                fullWidth
                size="small"
              />
              <TextField
                label="Mobile Number"
                name="mobile_number"
                                value={formData.mobile_number}
                onChange={handleChange}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                name="roles"
                value={formData.roles}
                onChange={handleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={selected =>
                  roles
                    .filter(r => selected.includes(r._id))
                    .map(r => r.name)
                    .join(', ')
                }
              >
                {roles.map(r => (
                  <MenuItem key={r._id} value={r._id}>
                    <Checkbox checked={formData.roles.includes(r._id)} size="small" />
                    <ListItemText primary={r.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button onClick={handleClose} sx={{ color: '#00bf63', fontSize: '0.875rem', px: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, fontSize: '0.875rem', px: 2 }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
